import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { getMetrics } from "../api/client.js";
import type { MetricEnv, MetricSeries, MetricsResponse } from "../api/types.js";
import { useApi } from "../lib/useApi.js";
import { formatBytesBinary, humanizeUnitValue } from "../lib/chart-math.js";
import { MetricAxisChart } from "../components/charts.js";
import { Card, Chip, EmptyState } from "../components/ui.js";

type ServiceTab = "ecs" | "rds" | "alb" | "nat";

interface MetricsPageProps {
  refreshKey: number;
  onGeneratedAt?: (iso: string | null) => void;
}

const ENV_DEFS: { env: MetricEnv; dot: string }[] = [
  { env: "staging", dot: "var(--acct-staging)" },
  { env: "prod", dot: "var(--acct-prod)" },
];

// The API serves a single fixed window: 3h at 5-minute resolution (no range param).
const RANGE_PILLS: { label: string; supported: boolean }[] = [
  { label: "3h", supported: true },
  { label: "24h", supported: false },
  { label: "7d", supported: false },
  { label: "30d", supported: false },
];

// CloudWatch stat per series: Average for gauges, Sum for the counters.
const SERIES_CAPTIONS: Record<string, string> = {
  ecsCpu: "5-min average",
  ecsMem: "5-min average",
  rdsCpu: "5-min average",
  rdsConn: "5-min average",
  rdsFree: "5-min average",
  albReq: "sum per 5 min",
  alb5xx: "sum per 5 min",
  albRt: "5-min average",
  natOut: "sum per 5 min",
};

const SERIES_COLORS: Record<string, string> = {
  alb5xx: "var(--tone-warn-dot)",
};

function SvgIcon({ children, size = 14 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: "none" }}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const TAB_ICONS: Record<ServiceTab, ReactNode> = {
  ecs: (
    <>
      <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </>
  ),
  rds: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14a9 3 0 0 0 18 0V5" />
      <path d="M3 12a9 3 0 0 0 18 0" />
    </>
  ),
  alb: (
    <>
      <path d="m16 3 4 4-4 4" />
      <path d="M20 7H4" />
      <path d="m8 21-4-4 4-4" />
      <path d="M4 17h16" />
    </>
  ),
  nat: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </>
  ),
};

const TAB_LABELS: Record<ServiceTab, string> = { ecs: "ECS", rds: "RDS", alb: "ALB", nat: "NAT" };

const segWrapStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 2,
  padding: 3,
  borderRadius: 9,
  background: "var(--surface)",
  border: "1px solid var(--border)",
};

const stripLabelStyle: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--fg-faint)",
};

const cardStatLabelStyle: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--fg-faint)",
};

interface SeriesDerived {
  now: number | null;
  avg: number | null;
  max: number | null;
}

function fmt(value: number | null, unit: string): string {
  return value === null ? "--" : humanizeUnitValue(value, unit);
}

function timeHM(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
}

// Short unit token for card headers ("%", "GiB", "ms", "count"). Bytes pick the
// binary suffix at the series' max magnitude so the header matches the axis scale.
function unitToken(series: MetricSeries): string {
  switch (series.unit) {
    case "Percent":
      return "%";
    case "Seconds":
    case "Milliseconds":
      return "ms";
    case "Bytes": {
      const ref =
        series.points.length > 0 ? Math.max(...series.points.map((p) => p.v)) : (series.latest ?? 0);
      return ref > 0 ? (formatBytesBinary(ref).split(" ")[1] ?? "bytes") : "bytes";
    }
    case "Count":
      return "count";
    default:
      return series.unit.toLowerCase();
  }
}

function deriveStats(series: MetricSeries): SeriesDerived {
  if (series.points.length === 0) return { now: series.latest, avg: null, max: null };
  const vals = series.points.map((p) => p.v);
  return {
    now: series.latest ?? vals[vals.length - 1],
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
    max: Math.max(...vals),
  };
}

function stripStats(list: MetricSeries[]): { label: string; value: string }[] {
  // Percent/Bytes/Seconds values embed their unit; only unitless counts need the
  // token in the label (which is uppercase-transformed, so "GiB" would mangle).
  const stats = list.map((s) => ({
    label: s.unit === "Count" ? `${s.label} · count` : s.label,
    value: fmt(deriveStats(s).now, s.unit),
  }));
  const first = list.find((s) => s.points.length > 0);
  if (stats.length < 3 && first) {
    const d = deriveStats(first);
    stats.push({ label: `${first.label} · avg`, value: fmt(d.avg, first.unit) });
    stats.push({ label: `${first.label} · max`, value: fmt(d.max, first.unit) });
  }
  return stats.slice(0, 5);
}

function xLabelsFor(series: MetricSeries): string[] | undefined {
  const pts = series.points;
  if (pts.length < 3) return undefined;
  return [timeHM(pts[0].t), timeHM(pts[Math.floor(pts.length / 2)].t), "now"];
}

function SeriesCard({ series }: { series: MetricSeries }) {
  const d = deriveStats(series);
  const stats = [
    { label: "Now", value: fmt(d.now, series.unit) },
    { label: "Avg", value: fmt(d.avg, series.unit) },
    { label: "Max", value: fmt(d.max, series.unit) },
  ];
  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 12, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap" }}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 13.5,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "var(--fg)",
          }}
        >
          {series.label}
        </span>
        <span className="mono" style={{ fontSize: 11.5, fontWeight: 500, color: "var(--fg-muted)" }}>
          · {unitToken(series)}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-faint)" }}>
          {SERIES_CAPTIONS[series.id] ?? ""}
        </span>
      </div>
      {series.points.length > 0 ? (
        <MetricAxisChart
          points={series.points.map((p) => p.v)}
          unit={series.unit}
          color={SERIES_COLORS[series.id] ?? "var(--accent-dot)"}
          height={150}
          xLabels={xLabelsFor(series)}
          xLabelFor={(i) => timeHM(series.points[i]?.t ?? "")}
        />
      ) : (
        <div
          style={{
            height: 150,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--fg-faint)",
            background: "var(--bg-subtle)",
            border: "1px dashed var(--border)",
            borderRadius: 10,
          }}
        >
          no datapoints in window
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 22,
          flexWrap: "wrap",
          borderTop: "1px solid var(--divider)",
          paddingTop: 11,
        }}
      >
        {stats.map((s) => (
          <span key={s.label} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <span style={cardStatLabelStyle}>{s.label}</span>
            <span className="mono" style={{ fontSize: 13, fontWeight: 500, color: "var(--fg)" }}>
              {s.value}
            </span>
          </span>
        ))}
      </div>
    </Card>
  );
}

export function MetricsPage({ refreshKey, onGeneratedAt }: MetricsPageProps) {
  const [env, setEnv] = useState<MetricEnv>("staging");
  const [tab, setTab] = useState<ServiceTab>("ecs");
  const loader = useCallback(() => getMetrics(env), [env]);
  const { data, loading, error } = useApi<MetricsResponse>(loader, [env], refreshKey);

  useEffect(() => {
    onGeneratedAt?.(data?.generatedAt ?? null);
  }, [data, onGeneratedAt]);

  const grouped = useMemo(() => {
    const series = data?.series ?? [];
    const byTab: Record<ServiceTab, MetricSeries[]> = { ecs: [], rds: [], alb: [], nat: [] };
    for (const s of series) {
      if (s.id.startsWith("ecs")) byTab.ecs.push(s);
      else if (s.id.startsWith("rds")) byTab.rds.push(s);
      else if (s.id.startsWith("alb")) byTab.alb.push(s);
      else if (s.id.startsWith("nat")) byTab.nat.push(s);
    }
    return byTab;
  }, [data]);

  // NAT dimensions are not resolvable server-side yet; hide the tab until real datapoints show up.
  const natAvailable = grouped.nat.some((s) => s.points.length > 0);
  const tabs: ServiceTab[] = natAvailable ? ["ecs", "rds", "alb", "nat"] : ["ecs", "rds", "alb"];

  useEffect(() => {
    if (tab === "nat" && !natAvailable) setTab("ecs");
  }, [tab, natAvailable]);

  const activeSeries = grouped[tab];
  const stats = stripStats(activeSeries);
  const tabEmpty = activeSeries.every((s) => s.points.length === 0);
  const errors = data?.errors ?? [];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={segWrapStyle} role="tablist" aria-label="Service">
          {tabs.map((t) => {
            const active = t === tab;
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "6px 13px",
                  borderRadius: 7,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: 12.5,
                  fontWeight: 600,
                  background: active ? "var(--accent-bg)" : "transparent",
                  color: active ? "var(--accent)" : "var(--fg-subtle)",
                }}
              >
                <SvgIcon>{TAB_ICONS[t]}</SvgIcon>
                {TAB_LABELS[t]}
              </button>
            );
          })}
        </div>
        {ENV_DEFS.map((e) => (
          <Chip key={e.env} active={env === e.env} onClick={() => setEnv(e.env)} dot={e.dot}>
            {e.env}
          </Chip>
        ))}
        <span
          style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-faint)", whiteSpace: "nowrap" }}
        >
          3-hour window · 5-minute resolution
        </span>
        <div style={segWrapStyle} aria-label="Range">
          {RANGE_PILLS.map((r) => (
            <button
              key={r.label}
              type="button"
              disabled={!r.supported}
              title={r.supported ? undefined : "Only the 3h window is served by the API"}
              style={{
                padding: "5px 12px",
                borderRadius: 7,
                border: "none",
                cursor: r.supported ? "pointer" : "not-allowed",
                fontFamily: "var(--font-body)",
                fontSize: 12,
                fontWeight: 600,
                background: r.supported ? "var(--accent-bg)" : "transparent",
                color: r.supported ? "var(--accent)" : "var(--fg-faint)",
                opacity: r.supported ? 1 : 0.55,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data && (
        <Card>
          <div className="state" role="status">
            <span className="spinner" aria-hidden="true" /> <span>Loading {env} metrics...</span>
          </div>
        </Card>
      )}
      {error && !data && (
        <Card>
          <div className="state state--error" role="alert">
            {error}
          </div>
        </Card>
      )}

      {data && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            opacity: loading ? 0.55 : 1,
            transition: "opacity var(--dur-fast) var(--ease-out)",
          }}
        >
          <Card
            style={{
              display: "flex",
              alignItems: "center",
              gap: 26,
              flexWrap: "wrap",
              padding: "13px 20px",
            }}
          >
            {stats.map((s) => (
              <span key={s.label} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <span style={stripLabelStyle}>{s.label}</span>
                <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: "var(--fg)" }}>
                  {s.value}
                </span>
              </span>
            ))}
          </Card>

          {errors.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "13px 16px",
                borderRadius: 16,
                background: "var(--tone-warn-bg)",
                border: "1px solid var(--border)",
              }}
            >
              <span style={{ color: "var(--tone-warn-fg)", display: "inline-flex" }}>
                <SvgIcon size={17}>
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </SvgIcon>
              </span>
              <span style={{ flex: 1, fontSize: 12.5, color: "var(--fg-muted)" }}>
                {errors.map((e) => `${e.scope}: ${e.message}`).join(" · ")}
              </span>
            </div>
          )}

          {tabEmpty ? (
            <div
              style={{
                background: "var(--surface)",
                border: "1px dashed var(--border-strong)",
                borderRadius: 16,
                padding: "40px 20px",
              }}
            >
              <EmptyState
                title={`No ${TAB_LABELS[tab]} datapoints for ${env}`}
                message="CloudWatch returned no data for this service in the last 3 hours."
                icon={<SvgIcon size={19}>{TAB_ICONS[tab]}</SvgIcon>}
                tone="nodata"
              />
            </div>
          ) : (
            <div className="grid-series">
              {activeSeries.map((s) => (
                <SeriesCard key={s.id} series={s} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
